import { useEffect, useRef } from 'react'
import { toast } from 'sonner'
import { onForegroundMessage, requestFcmToken } from '@/lib/firebase'
import { playNotificationSound } from '@/lib/notification-sound'
import { fcmNotificationsApi } from '@/services/fcm-notifications'

const REGISTERED_TOKEN_STORAGE_KEY = 'fcm_registered_token'
const FCM_TOKEN_STORAGE_KEY = 'fcm_token'

/**
 * Requests notification permission + an FCM token once per authenticated session,
 * registers it with the backend, and shows a toast for messages that arrive while
 * this tab is open (background/closed delivery is up to the browser + backend payload
 * shape -- see public/firebase-messaging-sw.js).
 */
export function useFcmNotifications(serviceInstanceId: string | null | undefined, enabled: boolean): void {
  const serviceInstanceIdRef = useRef(serviceInstanceId)
  serviceInstanceIdRef.current = serviceInstanceId

  useEffect(() => {
    if (!enabled || typeof window === 'undefined' || !('Notification' in window)) {
      return
    }

    let cancelled = false

    void (async () => {
      const token = await requestFcmToken()
      if (!token || cancelled) {
        return
      }

      window.localStorage.setItem(FCM_TOKEN_STORAGE_KEY, token)

      if (window.localStorage.getItem(REGISTERED_TOKEN_STORAGE_KEY) === token) {
        return
      }

      try {
        await fcmNotificationsApi.registerToken({
          token,
          serviceInstanceId: serviceInstanceIdRef.current,
        })
        window.localStorage.setItem(REGISTERED_TOKEN_STORAGE_KEY, token)
      } catch (error) {
        console.error('[fcm] Failed to register device token', error)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [enabled])

  useEffect(() => {
    if (!enabled) {
      return
    }

    return onForegroundMessage((payload) => {
      const title = payload.notification?.title || payload.data?.title || 'New notification'
      const body = payload.notification?.body || payload.data?.body || ''
      playNotificationSound()
      toast(title, body ? { description: body } : undefined)
    })
  }, [enabled])
}
